// Keeps one game in step with the server, and keeps working without signal.
//
// The browser stores the last state it got from the server plus the changes it has not had
// confirmed yet (localStorage, per link). What the player sees is that state with those changes on
// top, so a tap shows at once, online or not. Changes go out over POST as soon as there is a
// connection; the server's state comes back over Server-Sent Events after every change by anyone.
import { reportError } from './common/telemetry.ts';
import { applyOp, cloneState, normalizeState, parseOp, type GameState, type Op } from '../shared/game.ts';

export type Status = 'connecting' | 'live' | 'offline' | 'gone';

interface Saved {
  name: string;
  /** The team this link counts for; null on the watch-only link. */
  team: number | null;
  version: number;
  state: GameState;
  /** Changes not yet confirmed by the server, oldest first. */
  pending: Op[];
}

export interface LastGame {
  token: string;
  name: string;
}

const KEY_PREFIX = 'pubgolf:g:';
const LAST_KEY = 'pubgolf:last';
const FLUSH_DELAY_MS = 250;
const MAX_RETRY_MS = 15_000;

function sameTarget(a: Op, b: Op): boolean {
  if (a.t !== b.t || a.team !== b.team) return false;
  if (a.t === 'score' && b.t === 'score') return a.hole === b.hole;
  if (a.t === 'player' && b.t === 'player') return a.player === b.player;
  return true;
}

function load<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function store(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the game still works while the page is open.
  }
}

export function lastGame(): LastGame | null {
  return load<LastGame>(LAST_KEY);
}

export class GameSync {
  readonly token: string;
  status: Status = 'connecting';
  private saved: Saved | null;
  /** Sent, waiting for the answer. */
  private inflight: Op[] = [];
  private events: EventSource | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private retryMs = 0;
  private streamOpen = false;
  private readonly onChange: (remote: boolean) => void;

  constructor(token: string, onChange: (remote: boolean) => void) {
    this.token = token;
    this.onChange = onChange;
    this.saved = load<Saved>(KEY_PREFIX + token);
    if (this.saved) {
      // A copy kept by an older version of the app: bring the state into the current shape and drop
      // waiting changes that no longer apply (a score per player).
      const state = normalizeState(this.saved.state);
      const pending = Array.isArray(this.saved.pending) ? this.saved.pending : [];
      this.saved = { ...this.saved, state, pending: pending.map((op) => parseOp(op, state)).filter((op): op is Op => op !== null) };
    }
  }

  /** The game as this player sees it: the server's state with the unconfirmed changes on top. Null before the first load. */
  view(): GameState | null {
    if (!this.saved) return null;
    const state = cloneState(this.saved.state);
    for (const op of [...this.inflight, ...this.saved.pending]) applyOp(state, op);
    return state;
  }

  get name(): string | null {
    return this.saved?.name ?? null;
  }

  get team(): number | null {
    return this.saved?.team ?? null;
  }

  get loaded(): boolean {
    return this.saved !== null;
  }

  get waiting(): number {
    return this.inflight.length + (this.saved?.pending.length ?? 0);
  }

  start(): void {
    if (this.saved) store(LAST_KEY, { token: this.token, name: this.saved.name } satisfies LastGame);
    void this.refresh();
    this.connect();
    window.addEventListener('online', () => {
      this.reconnectIfClosed();
      this.flushSoon(0);
    });
    window.addEventListener('offline', () => this.setStatus());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      // Phones suspend pages in the background; catch up when the page is shown again.
      this.reconnectIfClosed();
      void this.refresh();
      this.flushSoon(0);
    });
  }

  /** A change by this player: shown at once, sent when possible. */
  change(op: Op): void {
    if (!this.saved || this.status === 'gone') return;
    // A later change to the same score or name replaces the earlier one still waiting.
    this.saved.pending = this.saved.pending.filter((p) => !sameTarget(p, op));
    this.saved.pending.push(op);
    this.persist();
    this.onChange(false);
    this.flushSoon(FLUSH_DELAY_MS);
  }

  private persist(): void {
    if (!this.saved) return;
    // In-flight changes are kept too: if the page closes before the answer, they are sent again.
    store(KEY_PREFIX + this.token, { ...this.saved, pending: [...this.inflight, ...this.saved.pending] });
  }

  /** Takes the server's state when it is newer than what we have. */
  private accept(payload: { name: string; version: number; state: GameState; team?: number | null }): boolean {
    if (this.saved && payload.version < this.saved.version) return false;
    const fresh = !this.saved;
    this.saved = {
      name: payload.name,
      team: payload.team !== undefined ? payload.team : (this.saved?.team ?? null),
      version: payload.version,
      state: normalizeState(payload.state),
      pending: this.saved?.pending ?? [],
    };
    this.persist();
    if (fresh || payload.team !== undefined) store(LAST_KEY, { token: this.token, name: payload.name } satisfies LastGame);
    return true;
  }

  private gone(): void {
    this.status = 'gone';
    this.events?.close();
    this.events = null;
    if (this.saved) {
      this.saved.pending = [];
      this.inflight = [];
    }
    try {
      localStorage.removeItem(KEY_PREFIX + this.token);
      if (lastGame()?.token === this.token) localStorage.removeItem(LAST_KEY);
    } catch {
      // ignore
    }
    this.onChange(true);
  }

  private setStatus(): void {
    if (this.status === 'gone') return;
    const next: Status = this.streamOpen && navigator.onLine ? 'live' : this.saved || !navigator.onLine ? 'offline' : 'connecting';
    if (next !== this.status) {
      this.status = next;
      this.onChange(false);
    }
  }

  private async refresh(): Promise<void> {
    if (this.status === 'gone') return;
    try {
      const response = await fetch(`/api/g/${this.token}`, { cache: 'no-store' });
      if (response.status === 404) return this.gone();
      if (!response.ok) return;
      const body = (await response.json()) as { name: string; version: number; state: GameState; team: number | null };
      if (this.accept(body)) this.onChange(true);
      this.setStatus();
    } catch {
      this.setStatus();
    }
  }

  private connect(): void {
    if (this.status === 'gone') return;
    const events = new EventSource(`/api/g/${this.token}/events`);
    this.events = events;
    events.addEventListener('open', () => {
      this.streamOpen = true;
      this.setStatus();
      this.flushSoon(0);
    });
    events.addEventListener('state', (event) => {
      this.streamOpen = true;
      try {
        if (this.accept(JSON.parse((event as MessageEvent<string>).data))) this.onChange(true);
      } catch (error) {
        reportError(error, { where: 'sync-event' });
      }
      this.setStatus();
    });
    events.addEventListener('gone', () => this.gone());
    events.addEventListener('error', () => {
      this.streamOpen = false;
      this.setStatus();
      // A refused stream (game deleted, rate limit) is not retried by the browser: check and retry ourselves.
      if (events.readyState === EventSource.CLOSED) {
        void this.refresh();
        setTimeout(() => this.reconnectIfClosed(), 5000);
      }
    });
  }

  private reconnectIfClosed(): void {
    if (this.status === 'gone') return;
    if (!this.events || this.events.readyState === EventSource.CLOSED) {
      this.events?.close();
      this.connect();
    }
  }

  private flushSoon(delay: number): void {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, delay);
  }

  private async flush(): Promise<void> {
    if (!this.saved || this.inflight.length > 0 || this.saved.pending.length === 0 || this.status === 'gone') return;
    if (this.saved.team === null) {
      this.saved.pending = [];
      this.persist();
      return;
    }
    this.inflight = this.saved.pending;
    this.saved.pending = [];
    let retry = false;
    try {
      const response = await fetch(`/api/g/${this.token}/ops`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ops: this.inflight }),
        cache: 'no-store',
      });
      if (response.ok) {
        const body = (await response.json()) as { name: string; version: number; state: GameState };
        this.inflight = [];
        this.accept(body);
        this.retryMs = 0;
        this.onChange(false);
      } else if (response.status === 404) {
        return this.gone();
      } else if (response.status === 400 || response.status === 403) {
        // Not allowed or not valid: these changes can never be applied, drop them.
        reportError(new Error(`changes refused: ${response.status}`), { where: 'sync-flush' });
        this.inflight = [];
        await this.refresh();
      } else retry = true;
    } catch {
      retry = true;
    }
    if (retry && this.saved) {
      this.saved.pending = [...this.inflight, ...this.saved.pending];
      this.inflight = [];
      this.retryMs = Math.min(MAX_RETRY_MS, this.retryMs ? this.retryMs * 2 : 2000);
      this.flushSoon(this.retryMs);
    } else if (this.saved && this.saved.pending.length > 0) this.flushSoon(0);
    this.persist();
    this.setStatus();
  }
}
